import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type { ReleaseDetail, ReleaseGate, ReleaseReadiness } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";

/**
 * Accepting the risk of a failed gate (§23).
 *
 * <p>The wording throughout is deliberate. This is not "override" in the sense of making a
 * problem go away — the gate goes on saying Fail forever, and the release is labelled as having
 * shipped with a waiver. What is being recorded is that a named person looked at a specific
 * failure and decided to go anyway, which is the thing an incident review three months later is
 * trying to establish.
 *
 * <p>Both fields are required because they answer different questions: why this is happening now,
 * and what is actually being risked. A waiver reading only "needed for the demo" tells a later
 * reader nothing about what might break.
 */
export function GateOverrideDrawer({
  release,
  readiness,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  readiness: ReleaseReadiness | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [gate, setGate] = useState<ReleaseGate | "">("");
  const [reason, setReason] = useState("");
  const [riskAcceptance, setRiskAcceptance] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  const blocking = (readiness?.gates ?? []).filter((entry) => entry.blocking);

  useEffect(() => {
    if (open) {
      setGate(blocking.length === 1 ? blocking[0]!.gate : "");
      setReason("");
      setRiskAcceptance("");
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chosen = blocking.find((entry) => entry.gate === gate);
  const canConfirm = gate !== "" && reason.trim() !== "" && riskAcceptance.trim() !== "";

  const submit = () =>
    void run(
      () =>
        api.post(`/api/v1/releases/${release.id}/gate-overrides`, {
          gate,
          reason: reason.trim(),
          riskAcceptance: riskAcceptance.trim(),
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Accept a risk"
      subtitle={`${release.releaseKey} — ${release.releaseName}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canConfirm} loading={saving}>
            {saving ? "Recording…" : "Accept the risk"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <InlineAlert tone="warning">
          This does not make the failure go away. The gate will go on showing what it found, and
          this release will be recorded as <strong>released with a waiver</strong> — permanently,
          with your name against it.
        </InlineAlert>

        {blocking.length === 0 ? (
          <p className="text-sm text-ink-600">Nothing is currently blocking this release.</p>
        ) : (
          <>
            <Field label="Which gate" required>
              <Select
                value={gate}
                onChange={(event) => setGate(event.target.value as ReleaseGate | "")}
              >
                <option value="">Choose a gate…</option>
                {blocking.map((entry) => (
                  <option key={entry.gate} value={entry.gate}>
                    {entry.label}
                  </option>
                ))}
              </Select>
            </Field>

            {chosen?.summary && (
              <div className="rounded-lg bg-ink-50 px-3 py-2 text-sm">
                <p className="text-xs font-medium text-ink-500">What it found</p>
                <p className="mt-0.5 text-ink-800">{chosen.summary}</p>
              </div>
            )}

            <Field label="Why now" required hint="What makes it right to go anyway, today.">
              <Textarea
                rows={2}
                value={reason}
                placeholder="Customer demo is tomorrow morning and this path is not part of it."
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>

            <Field
              label="What is being risked"
              required
              hint="What could go wrong, and who agreed to carry it."
            >
              <Textarea
                rows={3}
                value={riskAcceptance}
                placeholder="Cosmetic only — the retry banner renders twice. Accepted by the delivery board; a fix ships in 2.2."
                onChange={(event) => setRiskAcceptance(event.target.value)}
              />
            </Field>
          </>
        )}
      </div>
    </Drawer>
  );
}
